import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  Shield,
  UserCheck,
  Loader2,
  X,
  Key,
} from "lucide-react";
import { usersApi, rolesApi, User } from "@/api/client";

export default function UsersSettings() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showResetPassword, setShowResetPassword] = useState<string | null>(
    null,
  );

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    is_active: true,
    is_approver: false,
    is_org_admin: false,
    role_ids: [] as string[],
    primary_role_id: "",
  });

  const [newPassword, setNewPassword] = useState("");

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: usersApi.list,
  });

  const { data: rolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesApi.list(),
  });

  const users = usersData?.users || [];
  const roles = rolesData?.roles || [];

  const createMutation = useMutation({
    mutationKey: ["createUser"],
    mutationFn: usersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      handleCancel();
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateUser"],
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof usersApi.update>[1]) =>
      usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      handleCancel();
    },
  });

  const updateRolesMutation = useMutation({
    mutationKey: ["updateUser"],
    mutationFn: ({
      id,
      role_ids,
      primary_role_id,
    }: {
      id: string;
      role_ids: string[];
      primary_role_id?: string;
    }) => usersApi.updateRoles(id, { role_ids, primary_role_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteUser"],
    mutationFn: usersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationKey: ["resetPassword"],
    mutationFn: ({ id, new_password }: { id: string; new_password: string }) =>
      usersApi.resetPassword(id, { new_password }),
    onSuccess: () => {
      setShowResetPassword(null);
      setNewPassword("");
    },
  });

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      email: "",
      name: "",
      password: "",
      is_active: true,
      is_approver: false,
      is_org_admin: false,
      role_ids: [],
      primary_role_id: "",
    });
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: "",
      is_active: user.is_active,
      is_approver: user.is_approver,
      is_org_admin: user.is_org_admin,
      role_ids: user.roles?.map((r) => r.role_id) || [],
      primary_role_id: user.roles?.find((r) => r.is_primary)?.role_id || "",
    });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingUser) {
      // Update user
      updateMutation.mutate({
        id: editingUser.id,
        email: formData.email,
        name: formData.name,
        is_active: formData.is_active,
        is_approver: formData.is_approver,
        is_org_admin: formData.is_org_admin,
      });

      // Update roles separately if changed
      const currentRoleIds = editingUser.roles?.map((r) => r.role_id) || [];
      const rolesChanged =
        JSON.stringify(currentRoleIds.sort()) !==
        JSON.stringify(formData.role_ids.sort());
      if (rolesChanged) {
        updateRolesMutation.mutate({
          id: editingUser.id,
          role_ids: formData.role_ids,
          primary_role_id: formData.primary_role_id || undefined,
        });
      }
    } else {
      // Create user
      createMutation.mutate({
        email: formData.email,
        name: formData.name,
        password: formData.password,
        is_approver: formData.is_approver,
        is_org_admin: formData.is_org_admin,
        role_ids: formData.role_ids,
      });
    }
  };

  const handleDelete = (user: User) => {
    if (
      confirm(
        `Are you sure you want to delete "${user.name}"? This action cannot be undone.`,
      )
    ) {
      deleteMutation.mutate(user.id);
    }
  };

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (showResetPassword && newPassword.length >= 8) {
      resetPasswordMutation.mutate({
        id: showResetPassword,
        new_password: newPassword,
      });
    }
  };

  const toggleRole = (roleId: string) => {
    setFormData((prev) => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((id) => id !== roleId)
        : [...prev.role_ids, roleId],
    }));
  };

  if (usersLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage users, roles, and permissions
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add User
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="card mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="input"
                  required
                />
              </div>
            </div>

            {!editingUser && (
              <div>
                <label className="label">Password</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="input"
                  required
                  minLength={8}
                  placeholder="Minimum 8 characters"
                />
              </div>
            )}

            <div>
              <label className="label">Roles</label>
              <div className="flex flex-wrap gap-2">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => toggleRole(role.id)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      formData.role_ids.includes(role.id)
                        ? "bg-blue-100 border-blue-300 text-blue-800"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {role.name}
                  </button>
                ))}
              </div>
              {roles.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  No roles defined. Create roles in User Roles settings first.
                </p>
              )}
            </div>

            {formData.role_ids.length > 1 && (
              <div>
                <label className="label">Primary Role</label>
                <select
                  value={formData.primary_role_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      primary_role_id: e.target.value,
                    })
                  }
                  className="input"
                >
                  <option value="">Select primary role</option>
                  {formData.role_ids.map((roleId) => {
                    const role = roles.find((r) => r.id === roleId);
                    return role ? (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ) : null;
                  })}
                </select>
              </div>
            )}

            <div className="flex gap-6">
              {editingUser && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_approver}
                  onChange={(e) =>
                    setFormData({ ...formData, is_approver: e.target.checked })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Can approve deployments
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_org_admin}
                  onChange={(e) =>
                    setFormData({ ...formData, is_org_admin: e.target.checked })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Organization admin
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="btn btn-primary"
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editingUser ? (
                  "Update User"
                ) : (
                  "Create User"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Reset Password</h3>
              <button
                onClick={() => {
                  setShowResetPassword(null);
                  setNewPassword("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="mb-4">
                <label className="label">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                  required
                  minLength={8}
                  placeholder="Minimum 8 characters"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPassword(null);
                    setNewPassword("");
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    resetPasswordMutation.isPending || newPassword.length < 8
                  }
                  className="btn btn-primary"
                >
                  {resetPasswordMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Reset Password"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Roles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Permissions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr
                key={user.id}
                className={!user.is_active ? "bg-gray-50" : undefined}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {user.name}
                    </div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {user.roles?.map((role) => (
                      <span
                        key={role.id}
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          role.is_primary
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {role.role_name}
                      </span>
                    ))}
                    {(!user.roles || user.roles.length === 0) && (
                      <span className="text-xs text-gray-400">No roles</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-1">
                    {user.is_org_admin && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Shield className="h-3 w-3" />
                        Admin
                      </span>
                    )}
                    {user.is_approver && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        <UserCheck className="h-3 w-3" />
                        Approver
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      user.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {user.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowResetPassword(user.id)}
                      className="text-gray-400 hover:text-blue-600"
                      title="Reset Password"
                    >
                      <Key className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(user)}
                      className="text-gray-400 hover:text-blue-600"
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
